import { useTranslation } from 'react-i18next';
import React from 'react';
import { cn, currencyFormatter } from '@jobcost-monorepo/utils';
import { IFinancialReport } from '../types';

interface IProps {
  dataList: IFinancialReport[];
}

function groupByResource(
  reports: IFinancialReport[],
): Map<number, IFinancialReport[]> {
  return reports.reduce((groupedData, report) => {
    const resourceId = report.resource.id;

    if (!groupedData.has(resourceId)) {
      groupedData.set(resourceId, []);
    }
    groupedData.get(resourceId)?.push(report);

    return groupedData;
  }, new Map<number, IFinancialReport[]>());
}

const FinancialReportBody = React.forwardRef<HTMLDivElement, IProps>(
  ({ dataList }, ref) => {
    const { t } = useTranslation('financial-report');
    const dataGroupedByResource = groupByResource(dataList);
    let totalCost = 0;
    let totalRevenue = 0;

    if (dataList.length === 0) return <div />;
    return (
      <div ref={ref} className="size-full">
        {Array.from(dataGroupedByResource.entries()).map(
          ([resourceId, reports]) => {
            const resourceName = reports[0].resource.name;
            let cost = 0;
            let revenue = 0;

            return (
              <div key={resourceId} className="mb-8">
                <h2 className="text-lg font-bold">{resourceName}</h2>
                <div className="w-full min-w-[700px] pr-3">
                  <div className="table w-full rounded-lg border-t border-r border-black dark:border-grey50">
                    <div className="table-header-group">
                      <div className="table-row">
                        <div className="table-cell rounded-t-lg border-l p-2 font-bold w-3/12 border-black dark:border-grey50">
                          {t('table.job')}
                        </div>
                        <div className="table-cell p-2 font-bold w-3/12">
                          {t('table.order')}
                        </div>
                        <div className="table-cell text-right p-2 font-bold w-2/12">
                          {t('table.totalCost')}
                        </div>
                        <div className="table-cell text-right p-2 font-bold w-2/12">
                          {t('table.totalRevenue')}
                        </div>
                        <div className="table-cell text-right p-2 font-bold w-2/12">
                          {t('table.totalProfit')}
                        </div>
                      </div>
                    </div>
                    <div className="table-row-group">
                      {reports.map((report, index) => {
                        cost = cost + report.totalCost;
                        revenue = revenue + report.totalRevenue;
                        totalCost = totalCost + cost;
                        totalRevenue = totalRevenue + revenue;
                        return (
                          <div
                            key={`${report.resource.id}-${report.job.id}`}
                            className={cn(
                              index % 2
                                ? `table-row bg-violet2 dark:bg-transparent`
                                : `table-row bg-violet5 dark:bg-darkGrey`,
                              reports.length - 1 === index && 'rounded-bl-lg',
                            )}
                          >
                            <div
                              className={cn(
                                'table-cell border-l p-2 w-3/12 border-black dark:border-grey50 break-words',
                                reports.length - 1 === index &&
                                  'border-b rounded-bl-lg',
                              )}
                            >
                              {report.job.name}
                            </div>
                            <div
                              className={cn(
                                'table-cell p-2 w-3/12',
                                reports.length - 1 === index &&
                                  'border-b border-black dark:border-grey50',
                              )}
                            >
                              {report.order.name}
                            </div>
                            <div className="table-cell p-2 text-right w-2/12">
                              {currencyFormatter({
                                value: report.totalCost,
                                options: {},
                              })}
                            </div>
                            <div className="table-cell p-2 text-right w-2/12">
                              {currencyFormatter({
                                value: report.totalRevenue,
                                options: {},
                              })}
                            </div>
                            <div className="table-cell p-2 text-right  w-2/12">
                              {currencyFormatter({
                                value: report.totalRevenue - report.totalCost,
                                options: {},
                              })}
                            </div>
                          </div>
                        );
                      })}
                      <div className={'table-row'}>
                        <div className="table-cell p-2 w-3/12"></div>
                        <div className="table-cell p-2 font-bold w-3/12 text-right">
                          {t('table.total')}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 text-right w-2/12 border-l border-b border-black dark:border-grey50 rounded-bl-lg',
                            reports.length % 2
                              ? `bg-violet2 dark:bg-transparent`
                              : `bg-violet5 dark:bg-darkGrey`,
                          )}
                        >
                          {currencyFormatter({
                            value: cost,
                            options: {},
                          })}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 text-right w-2/12 border-b border-black dark:border-grey50',
                            reports.length % 2
                              ? `bg-violet2 dark:bg-transparent`
                              : `bg-violet5 dark:bg-darkGrey`,
                          )}
                        >
                          {currencyFormatter({
                            value: revenue,
                            options: {},
                          })}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 text-right w-2/12 rounded-br-lg border-b border-black dark:border-grey50',
                            reports.length % 2
                              ? `bg-violet2 dark:bg-transparent`
                              : `bg-violet5 dark:bg-darkGrey`,
                          )}
                        >
                          {currencyFormatter({
                            value: revenue - cost,
                            options: {},
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          },
        )}
        <div className="pb-2">
          <h2 className="text-lg font-bold"> {t('table.total')}</h2>
          <div className="table w-full rounded-lg border-t border-black dark:border-grey50">
            <div className="table-header-group">
              <div className="table-row">
                <div className="table-cell rounded-t-lg border-l p-2 font-bold w-1/3 border-black dark:border-grey50">
                  {t('table.totalCost')}
                </div>
                <div className="table-cell p-2 font-bold w-1/3">
                  {t('table.totalRevenue')}
                </div>
                <div className="table-cell p-2 rounded-tr-lg border-r border-black dark:border-grey50 font-bold w-1/3">
                  {t('table.totalProfit')}
                </div>
              </div>
            </div>
            <div className="table-row-group">
              <div className="table-row">
                <div className="table-cell border-l rounded-bl-lg border-b p-2 w-3/12 border-black dark:border-grey50 bg-violet5 dark:bg-darkGrey">
                  {currencyFormatter({
                    value: totalCost,
                    options: {},
                  })}
                </div>
                <div className="table-cell p-2 w-3/12 border-b border-black dark:border-grey50 bg-violet5 dark:bg-darkGrey">
                  {currencyFormatter({
                    value: totalRevenue,
                    options: {},
                  })}
                </div>
                <div className="table-cell p-2 w-3/12 rounded-br-lg border-b border-r border-black dark:border-grey50 bg-violet5 dark:bg-darkGrey">
                  {currencyFormatter({
                    value: totalRevenue - totalCost,
                    options: {},
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

FinancialReportBody.displayName = 'FinancialReportBody';

export default FinancialReportBody;
